import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './HexCarousel.css';

/**
 * HexCarousel - A 3D hexagonal carousel for displaying products
 * during the analyzing phase. Shows 6 product slots arranged in a honeycomb pattern.
 * Center badge is STATIC (no spin). Only outer hex ring rotates during loading.
 */
const HexCarousel = ({ products = [], analysis = {}, isLoading = true, onProductClick }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Get the first 6 products for the hex grid
  const displayProducts = products.slice(0, 6);

  // Hex positions - arranged in a honeycomb pattern
  const hexPositions = [
    { x: 0, y: -1 },       // Top
    { x: 0.866, y: -0.5 }, // Top Right
    { x: 0.866, y: 0.5 },  // Bottom Right
    { x: 0, y: 1 },        // Bottom
    { x: -0.866, y: 0.5 }, // Bottom Left
    { x: -0.866, y: -0.5 }, // Top Left
  ];

  const handleProductClick = (e, product) => {
    e.stopPropagation();
    if (onProductClick && product) {
      onProductClick(product);
    }
  };

  const handleHexClick = (product) => {
    if (onProductClick && product) {
      onProductClick(product);
    }
  };

  return (
    <div className="hex-carousel-container hex-3d">
      {/* 3D Scene wrapper */}
      <div className="hex-3d-scene">
        {/* Central Analysis Display - STATIC, no spinning */}
        <div className="hex-center-3d">
          <motion.div
            className="analysis-badge-3d"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            {analysis.item_type && (
              <span className="analysis-item">{analysis.item_type}</span>
            )}
            {analysis.color && (
              <span className="analysis-color">{analysis.color}</span>
            )}
            {isLoading && (
              <div className="center-loading-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            )}
          </motion.div>
        </div>

        {/* 3D Hex Grid - only this rotates */}
        <motion.div
          className="hex-grid-3d"
          animate={isLoading ? {
            rotate: [0, 360],
          } : {
            rotate: 0,
          }}
          transition={isLoading ? {
            rotate: { duration: 20, repeat: Infinity, ease: "linear" },
          } : { duration: 0.3 }}
        >
          {hexPositions.map((pos, index) => {
            const product = displayProducts[index];
            const hasProduct = !!product;
            const isHovered = hoveredIndex === index;

            return (
              <motion.div
                key={index}
                className={`hex-cell-3d ${hasProduct ? 'has-product' : 'empty'} ${isHovered ? 'hovered' : ''}`}
                style={{
                  '--hex-x': `${pos.x * 130}px`,
                  '--hex-y': `${pos.y * 110}px`,
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: 1,
                  scale: isHovered ? 1.1 : 1,
                  x: pos.x * 130,
                  y: pos.y * 110,
                }}
                transition={{
                  delay: index * 0.08,
                  duration: 0.3,
                  type: "spring",
                  stiffness: 250
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => handleHexClick(product)}
              >
                <div className="hex-shape-3d">
                  <AnimatePresence mode="wait">
                    {hasProduct ? (
                      <motion.div
                        key={product.product_id || index}
                        className="hex-product-3d"
                        initial={{ opacity: 0, scale: 0.5, rotateY: -30 }}
                        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                        exit={{ opacity: 0, scale: 0.5, rotateY: 30 }}
                        transition={{ duration: 0.4 }}
                      >
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.product_name || 'Product'}
                            className="hex-product-image-3d"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="hex-product-fallback-3d" style={{ display: product.image_url ? 'none' : 'flex' }}>
                          👗
                        </div>

                        {/* Hover Overlay with Shop CTA */}
                        <motion.div
                          className="hex-overlay-3d"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: isHovered ? 1 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <motion.button
                            className="hex-shop-btn-3d"
                            onClick={(e) => handleProductClick(e, product)}
                            whileHover={{ scale: 1.1, boxShadow: "0 8px 25px rgba(125, 107, 160, 0.5)" }}
                            whileTap={{ scale: 0.95 }}
                          >
                            🛒 Shop Now
                          </motion.button>
                          <span className="hex-product-name">{product.product_name?.slice(0, 30) || 'View Product'}</span>
                        </motion.div>

                        {/* Price Badge */}
                        {product.price > 0 && (
                          <motion.div
                            className="hex-price-badge-3d"
                            animate={{ scale: isHovered ? 1.1 : 1 }}
                          >
                            ${typeof product.price === 'number' ? product.price.toFixed(0) : product.price}
                          </motion.div>
                        )}

                        {/* Click indicator */}
                        <div className="hex-click-hint">
                          <span>Click to shop</span>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key={`empty-${index}`}
                        className="hex-placeholder-3d"
                        animate={{
                          opacity: [0.3, 0.6, 0.3],
                          scale: [0.98, 1.02, 0.98],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: index * 0.2
                        }}
                      >
                        <div className="hex-shimmer-3d" />
                        <span className="hex-loading-icon">✨</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Products Found Counter */}
      <motion.div
        className="products-counter-3d"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <span className="counter-number">{products.length}</span>
        <span className="counter-label">products found</span>
      </motion.div>
    </div>
  );
};

export default HexCarousel;
